// SPDX-License-Identifier: GPL-3.0-or-later
//
// Prompts for the "import existing CV" flow:
//   1. profileImport — clean noisy PDF/DOCX text into a structured profile
//      summary the AI can later draw from.
//   2. templateImport — emit a Typst template that mirrors the uploaded CV's
//      layout, using the existing Mustache placeholder schema so the rest of
//      the pipeline (render.ts, template-adjust.ts) keeps working.

import { CV_JSON_SCHEMA } from '../schemas';

import type { CvParseKind } from '../../fs/cv-parse';

export interface CvImportInputs {
  rawText: string;
  sourceKind: CvParseKind;
}

export const profileImportSystemPrompt = `You are a careful CV cleaner. The user has uploaded their existing CV; the raw text was extracted by an automated parser and may be noisy (broken line breaks, header/footer artefacts, column-order jumbling on PDFs).

Your job: rewrite it as a clean, complete profile that captures EVERYTHING the parser preserved — every role, every bullet, every degree, every skill, every link. Do not summarise away content. Do not invent content the source doesn't support.

Structure the output as plain text with clear sections in this order, omitting any section the source CV doesn't cover:

  CONTACT
  (name, email, phone, location, links — one per line)

  SUMMARY
  (the candidate's professional summary / objective, if present)

  EXPERIENCE
  (for each role, in reverse-chronological order:
    Role — Company — Period
    - bullet
    - bullet)

  EDUCATION
  (Degree, School, Period, optional notes)

  SKILLS
  (grouped: "Languages: ...", "Tools: ...", etc.)

  OTHER
  (certifications, publications, volunteering, languages spoken — whatever the source CV included that doesn't fit above)

Rules:
- Output only the cleaned profile text. No commentary, no markdown fences, no apologies.
- Preserve dates exactly as written ("2022 — present", "Jan 2021 – Mar 2023").
- Fix obvious OCR-style glitches (joined words, dropped spaces, swapped column order) when you can do so unambiguously.
- If a field looks corrupt and you can't recover it, drop it rather than guess.
- Keep technical terms, acronyms, and proper nouns exactly as written.`;

export function profileImportUserPrompt(inputs: CvImportInputs): string {
  const sourceLabel: Record<CvParseKind, string> = {
    pdf: 'PDF',
    docx: 'DOCX (Word)',
    html: 'HTML',
    text: 'plain text',
  };
  return `<source-format>${sourceLabel[inputs.sourceKind]}</source-format>

<raw-cv-text>
${inputs.rawText.trim() || '(empty)'}
</raw-cv-text>

Produce the cleaned profile text. Output only the cleaned text.`;
}

export const templateImportSystemPrompt = `You receive the raw text of a user's existing CV (extracted from a PDF / DOCX / HTML / text file) and must produce a Typst template that mirrors its visual layout.

You output ONLY valid JSON of this exact shape:
{
  "template": string,   // full Typst source, using the Mustache placeholders below
  "notes"?: string      // optional one-liner: what layout choices you preserved
}

The template MUST use this Mustache placeholder schema — the rest of the app expects it. Do not invent new variable names.

${CV_JSON_SCHEMA}

Mustache syntax in the template:
- {{var}} — Typst-escaped substitution.
- {{{var}}} — raw substitution (use for URLs only).
- {{#section}}…{{/section}} — iterate an array. Inside the block, refer to fields directly (e.g. {{role}}) and use {{.}} for primitive array items like skills.items.
- Dot-paths like {{contact.email}} are supported.

Layout fidelity rules:
- Match the SOURCE CV's section ORDER (e.g. if Skills comes before Experience there, keep that order).
- Match heading style choices visible in the text: ALL CAPS section headings, ruled lines under headings, left-aligned name vs centred name, etc.
- Match the column structure if you can infer it (single-column is the safe default; only emit a two-column layout if the source clearly uses one and you can map it cleanly).
- Match spacing intensity (tight vs airy) and font choice direction (serif vs sans-serif). If unsure, use Libertinus Serif at 11pt with 2cm margins.
- Preserve subtle conventions like role-bold + italic-company, period right-aligned with #h(1fr), bulleted achievements with - lists.

Hard rules:
- Output ONLY the JSON object. No markdown fences, no commentary outside it.
- The template must be valid Typst that compiles on its own (with #set page, #set text, etc.).
- Every {{#x}} must have its matching {{/x}}.
- Never embed the source CV's literal content — only its layout / styling / section ordering. The placeholders are filled at generation time per-application.
- Do not invent variable names beyond the schema above.
- If a section in the source CV maps to no schema variable (e.g. "Hobbies"), omit it from the template rather than fabricate.`;

export function templateImportUserPrompt(inputs: CvImportInputs): string {
  const sourceLabel: Record<CvParseKind, string> = {
    pdf: 'PDF',
    docx: 'DOCX (Word)',
    html: 'HTML',
    text: 'plain text',
  };
  return `<source-format>${sourceLabel[inputs.sourceKind]}</source-format>

<raw-cv-text>
${inputs.rawText.trim() || '(empty)'}
</raw-cv-text>

Produce the Typst template that mirrors this CV's layout. Output only the JSON object.`;
}
